const USER_ROLE_ADMIN = 'Admin';
const USER_ROLE_SW_SALES = 'Merchant';

const USER_ROLE_SUNSOUL = 'Sun Soul';
const USER_ROLE_LICENSEE = 'Licensee Merchant';
const USER_ROLE_PERGOLAS = 'Pergola Dealer';
const USER_ROLE_SAUNAS = 'Sauna Dealer';
const USER_ROLE_DEALER = 'Dealer';

const FALLBACK_USER_ROLE = 'default';

const dealer_defaults = {
    canSeeCustomerDetails: false,
    canSeeOwnOrders: false,
    canSeeProntoOrders: false, // SW sales/admin only — never dealers or licensees
    canSearchCustomerDetails: false,
    alwaysSendNotificationEmail: true,
    shouldCreateZohoTaskForOrder: true,
    canMarkDelivered: false,
    shouldSeeDealerFields: true,
    createOrdersUnderUserAccount: false,
    onlyShowSunSoulProducts: false,
    onlyShowPergolas: false,
    onlyShowShowProducts: false,
    showSunSoulProducts: false,
    forceWholesaleTerritory: true,
    defaultSalesPerson: false,
    filterCondition: [
        'New',
        'New IT - Wholesale',
        'New - IT Wholesale',
        'New - Wholesale',
        'New IT',
        'Sold',
        'Sold - IT',
        'Forward Order'
    ],
    updateCondition: {
        'New': 'New - Wholesale',
        'New IT': 'New IT - Wholesale'
    },
    splitOrders: true,
    orderStatusOnCreation: 'New',
    createOpportunities: false,
    recordPayments: false
}

const permissions = {
    'default': {
        canSeeCustomerDetails: true,
        canSeeOwnOrders: true,
        canSeeProntoOrders: false,
        canSearchCustomerDetails: true,
        alwaysSendNotificationEmail: false,
        shouldCreateZohoTaskForOrder: false,
        canMarkDelivered: false,
        shouldSeeDealerFields: false,
        createOrdersUnderUserAccount: false,
        onlyShowSunSoulProducts: false, // Also replaces S&S product names
        onlyShowPergolas: false,
        showSunSoulProducts: false,
        filterCondition: false,
        forceWholesaleTerritory: false,
        updateCondition: false,
        defaultSalesPerson: false,
        splitOrders: true,
        orderStatusOnCreation: 'Pick',
        createOpportunities: false,
        recordPayments: false
    },
    [USER_ROLE_ADMIN]: {
        canMarkDelivered: true,
        canSeeProntoOrders: true,
        showSunSoulProducts: true,
        createOpportunities: true,
        //recordPayments: true
    },
    [USER_ROLE_SW_SALES]: {
        canSeeProntoOrders: true,
        showSunSoulProducts: true,
        filterConditionExclude: [
            'QA Hold',
            'Unappraised',
            'Inventory Hold'
        ],
        createOpportunities: true,
        //recordPayments: true
    },
    [USER_ROLE_SUNSOUL]: {
        ...dealer_defaults,
        replaceSunSoulProducts: true,
        onlyShowSunSoulProducts: true,
        showSunSoulProducts: true,
    },
    [USER_ROLE_LICENSEE]: {
        ...dealer_defaults,
        canSeeOwnOrders: true,
        canSeeCustomerDetails: true,
        createOrdersUnderUserAccount: true,
        defaultSalesPerson: 'dal',
        splitOrders: false,
        orderStatusOnCreation: 'New',
        createOpportunities: false
    },
    [USER_ROLE_SAUNAS]: {
        ...dealer_defaults,
        onlyShowSaunas: true,
        replaceSunSoulProducts: true,
        showSunSoulProducts: true
    },
    [USER_ROLE_PERGOLAS]: {
        ...dealer_defaults,
        onlyShowPergolas: true,
    },
    [USER_ROLE_DEALER]: {
        ...dealer_defaults,
        replaceSunSoulProducts: true,
        onlyShowSunSoulProducts: ['SUNSOUL', 'Pergolas', 'Dealer Saunas'],
        showSunSoulProducts: true,
    }
}

const userCan = (userRole, permission) => {
    if (typeof( userRole ) !== 'string' && typeof( userRole ) !== 'undefined' ) {
        throw new Error(`Invalid userRole: ${userRole}`);
    }
    if ( typeof( permissions[userRole] ) === 'undefined' ) {
        userRole = FALLBACK_USER_ROLE;
    }

    return typeof( permissions[userRole][permission] ) !== 'undefined' ? permissions[userRole][permission] : permissions['default'][permission];
};

export const isAdmin = userRole => userRole === USER_ROLE_ADMIN;

export const canSeeCustomerDetails = userRole => {
    return userCan(userRole, 'canSeeCustomerDetails');
};

export const canSeeOwnOrders = userRole => {
    return userCan(userRole, 'canSeeOwnOrders');
};

export const canSeeProntoOrders = userRole => {
    return userCan(userRole, 'canSeeProntoOrders');
};

export const canSearchCustomerDetails = userRole => {
    return userCan(userRole, 'canSearchCustomerDetails');
};

export const alwaysSendNotificationEmail = userRole => {
    return userCan(userRole, 'alwaysSendNotificationEmail');
};

export const shouldCreateZohoTaskForOrder = userRole => {
    return userCan(userRole, 'shouldCreateZohoTaskForOrder');
};

export const canMarkDelivered = userRole => {
    return userCan(userRole, 'canMarkDelivered');
};

export const shouldSeeDealerFields = userRole => {
    return userCan(userRole, 'shouldSeeDealerFields');
};

export const createOrdersUnderUserAccount = userRole => {
    return userCan(userRole, 'createOrdersUnderUserAccount');
};

export const onlyShowSunSoulProducts = userRole => {
    return userCan(userRole, 'onlyShowSunSoulProducts');
};

export const replaceSunSoulProducts = userRole => {
    return userCan(userRole, 'replaceSunSoulProducts');
};
export const onlyShowPergolas = userRole => {
    return userCan(userRole, 'onlyShowPergolas');
};

export const onlyShowSaunas = userRole => {
    return userCan(userRole, 'onlyShowSaunas');
};

export const onlyShowShowProducts = userRole => {
    return userCan(userRole, 'onlyShowShowProducts');
};

export const forceWholesaleTerritory = userRole => {
    return userCan(userRole, 'forceWholesaleTerritory');
};

export const updateConditionTo = (userRole, condition) => {
    const conditionMap = userCan(userRole, 'updateCondition');
    return conditionMap ? conditionMap[condition] : false;
};

export const showSunSoulProducts = userRole => {
    return userCan(userRole, 'showSunSoulProducts');
};

export const shouldSplitOrders = userRole => {
    return userCan(userRole, 'splitOrders');
};

export const filterCondition = userRole => {
    return userCan(userRole, 'filterCondition');
};

export const filterConditionExclude = userRole => {
    return userCan(userRole, 'filterConditionExclude');
};

export const getDefaultSalesPerson = userRole => {
    return userCan(userRole, 'defaultSalesPerson');
};

export const orderStatusOnCreation = userRole => {
    return userCan(userRole, 'orderStatusOnCreation');
};

export const shouldCreateOpportunities = userRole => {
    return userCan(userRole, 'createOpportunities');
};

export const shouldRecordPayments = userRole => {
    return userCan(userRole, 'recordPayments');
};